import type { CSSProperties, FC, ReactElement } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import styles from './HelpComponentWrapper.module.css';

interface HelpComponentWrapperProps {
  children: ReactElement;
  isOriginalHeight?: boolean;
  minWidth?: string;
  maxWidth?: string;
  style?: CSSProperties;
}

export const HelpComponentWrapper: FC<HelpComponentWrapperProps> = ({
  style,
  children,
  isOriginalHeight = true,
  minWidth,
  maxWidth,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const scaleComponent = useCallback((): void => {
    const childElement = containerRef.current?.children[0] as HTMLElement;
    if (!childElement) return;

    const containerWidth = containerRef.current?.offsetWidth;
    const childWidth = childElement.offsetWidth;

    if (containerWidth && childWidth > containerWidth) {
      const scale = containerWidth / childWidth;
      childElement.style.transform = `scale(${scale})`;
    } else {
      childElement.style.transform = 'none';
    }

    if (!isOriginalHeight) {
      const childHeight = childElement.getBoundingClientRect().height;
      containerRef.current!.style.height = `${childHeight}px`;
    }

    // This ensures that element resizes without flickering
    childElement.style.visibility = 'visible';
  }, [isOriginalHeight]);

  const overrideComponentMinWidth = useCallback(() => {
    if (minWidth && containerRef.current) {
      const childElement = containerRef.current.children[0] as HTMLElement;

      if (childElement) {
        childElement.style.minWidth = minWidth;
      }
    }
  }, [minWidth]);

  const overrideComponentMaxWidth = useCallback(() => {
    if (maxWidth && containerRef.current) {
      const childElement = containerRef.current.children[0] as HTMLElement;

      if (childElement) {
        childElement.style.maxWidth = maxWidth;
      }
    }
  }, [maxWidth]);

  // Helper to update min/max width
  const updateChildMinMax = useCallback(() => {
    overrideComponentMinWidth();
    overrideComponentMaxWidth();
  }, [overrideComponentMinWidth, overrideComponentMaxWidth]);

  // Helper to update child layout
  const updateChildLayout = useCallback(() => {
    scaleComponent();
    updateChildMinMax();
  }, [scaleComponent, updateChildMinMax]);

  useEffect(() => {
    const observedElement: HTMLDivElement | null = containerRef.current;

    // Set up ResizeObserver on the container
    if (observedElement) {
      resizeObserverRef.current = new ResizeObserver(() => {
        updateChildLayout();
      });
      resizeObserverRef.current.observe(observedElement);
    }
    updateChildLayout();

    window.addEventListener('resize', updateChildLayout, { passive: true });

    return (): void => {
      resizeObserverRef.current?.disconnect();
      window.removeEventListener('resize', updateChildLayout);
    };
  }, [updateChildLayout, updateChildMinMax]);

  return (
    <div ref={containerRef} className={styles.help__componentWrapper} style={style}>
      {children}
    </div>
  );
};
